import { useState, useRef, useEffect } from "react";

const API_URL =
  typeof process !== "undefined" && process.env?.NEXT_PUBLIC_API_URL
    ? process.env.NEXT_PUBLIC_API_URL
    : "";

const CRM_URL =
  typeof process !== "undefined" && process.env?.NEXT_PUBLIC_ADMIN_URL
    ? process.env.NEXT_PUBLIC_ADMIN_URL
    : "";

const OTP_FLOW_ENABLED = false;
const STEPS = OTP_FLOW_ENABLED ? ["phone", "otp", "form"] : ["phone", "form"];

const DEFAULT_CFG = {
  date: '4 April 2026',
  time: '4:00 PM IST',
  venueShort: 'Arya Auditorium, East of Kailash, New Delhi',
  mapsUrl: 'https://maps.app.goo.gl/U3VUiE8D794QSkm59',
};

export default function SeminarRegistrationModal({ isOpen, onClose }) {
  // step: "phone" | "otp" | "form" | "success"
  const [step, setStep] = useState("phone");

  // Phone + OTP state
  const [phone, setPhone] = useState("");
  const [otpDigits, setOtpDigits] = useState(["", "", "", ""]);
  const otpRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];
  const [resendTimer, setResendTimer] = useState(0);
  const [isOtpVerified, setIsOtpVerified] = useState(false);

  // Registration form state
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    parentPhone: "",
    email: "",
  });

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Live seminar config (date, time, venue)
  const [cfg, setCfg] = useState(DEFAULT_CFG);
  useEffect(() => {
    const base = CRM_URL || '';
    fetch(`${base}/api/seminar-config/neet-ug-2026-summit`)
      .then((r) => r.json())
      .then((json) => { if (json?.data) setCfg({ ...DEFAULT_CFG, ...json.data }); })
      .catch(() => { });
  }, []);

  // Reset all state when modal closes
  useEffect(() => {
    if (!isOpen) {
      const t = setTimeout(() => {
        setStep("phone");
        setPhone("");
        setOtpDigits(["", "", "", ""]);
        setForm({ firstName: "", lastName: "", parentPhone: "", email: "" });
        setError("");
        setResendTimer(0);
        setIsOtpVerified(false);
        setLoading(false);
      }, 300);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  // Resend OTP countdown
  useEffect(() => {
    if (resendTimer <= 0) return;
    const t = setTimeout(() => setResendTimer((p) => p - 1), 1000);
    return () => clearTimeout(t);
  }, [resendTimer]);

  // Lock body scroll
  useEffect(() => {
    if (isOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // Auto-focus first OTP box when step changes to otp
  useEffect(() => {
    if (step === "otp") {
      setTimeout(() => otpRefs[0].current?.focus(), 80);
    }
  }, [step]);

  /* ── API helpers ───────────────────────────────────────── */

  const sendOtp = async () => {
    if (isOtpVerified) return;

    if (phone.length !== 10) {
      setError("Please enter a valid 10-digit mobile number.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/auth/requestGuestOtp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mobile: phone }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok || data.message?.toLowerCase().includes("otp sent")) {
        setStep("otp");
        setResendTimer(30);
      } else {
        setError(data.message || "Failed to send OTP. Please try again.");
      }
    } catch {
      setError("Network error. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const next = [...otpDigits];
    next[index] = value;
    setOtpDigits(next);
    setError("");
    if (value && index < 3) otpRefs[index + 1].current?.focus();
    else if (!value && index > 0) otpRefs[index - 1].current?.focus();
  };

  const verifyOtp = async () => {
    const otp = otpDigits.join("");
    if (otp.length !== 4) {
      setError("Please enter the complete 4-digit OTP.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/auth/verifyGuestOtp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mobile: phone, otp }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && (data.success || data.message?.toLowerCase().includes("verified"))) {
        setIsOtpVerified(true);
        setStep("form");
      } else {
        setError(data.message || "Invalid OTP. Please try again.");
      }
    } catch {
      setError("Network error. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  const continueWithoutOtp = () => {
    if (phone.length !== 10) {
      setError("Please enter a valid 10-digit mobile number.");
      return;
    }
    setError("");
    setStep("form");
  };

  const handleRegister = async () => {
    if (!form.firstName.trim()) {
      setError("Please enter your first name.");
      return;
    }
    if (!form.lastName.trim()) {
      setError("Please enter your last name.");
      return;
    }
    if (
      !form.email.trim() ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)
    ) {
      setError("Please enter a valid email address.");
      return;
    }
    if (form.parentPhone && form.parentPhone.length !== 10) {
      setError("Please enter a valid 10-digit parent/student number.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${CRM_URL}/api/seminar-registrations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          phone: "+91" + phone,
          email: form.email.trim(),
          parentPhone: form.parentPhone ? "+91" + form.parentPhone : undefined,
          seminarId: "neet-ug-2026-summit",
          seminarTitle: "NEET UG 2026 Admission Summit",
          seminarDate: cfg.date,
          source: "Seminar Registration",
          tags: "Seminar Registration, NEET UG 2026",
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok || data.success) {
        setStep("success");
      } else if (res.status === 409) {
        setError(data.message || "You are already registered for this seminar.");
      } else {
        setError(data.message || "Registration failed. Please try again.");
      }
    } catch {
      setError("Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  /* ── Keyboard shortcuts ────────────────────────────────── */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    if (isOpen) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const stepIndex = STEPS.indexOf(step);

  /* ── Render ────────────────────────────────────────────── */
  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(6px)" }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top accent bar */}
        <div className="h-1.5 w-full bg-gradient-to-r from-[#ff9245] to-[#ffb347]" />

        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3.5 right-4 text-zinc-400 hover:text-zinc-700 transition-colors"
          aria-label="Close modal"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        <div className="px-7 py-8">
          {/* Step indicator — hidden on success */}
          {step !== "success" && (
            <div className="flex items-center gap-1 mb-7">
              {STEPS.map((s, i) => (
                <div key={s} className="flex items-center gap-1">
                  <div
                    className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 ${step === s
                        ? "bg-[#ff9245] text-white scale-110 shadow-md"
                        : stepIndex > i
                          ? "bg-green-500 text-white"
                          : "bg-zinc-100 text-zinc-400"
                      }`}
                  >
                    {stepIndex > i ? (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-3.5 w-3.5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={3}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    ) : (
                      i + 1
                    )}
                  </div>
                  {i < STEPS.length - 1 && (
                    <div
                      className={`h-px w-10 transition-all duration-300 ${stepIndex > i ? "bg-green-400" : "bg-zinc-200"
                        }`}
                    />
                  )}
                </div>
              ))}
              <p className="ml-auto text-xs text-zinc-400 font-medium">
                Step {Math.min(stepIndex + 1, STEPS.length)} of {STEPS.length}
              </p>
            </div>
          )}

          {/* ── STEP 1: Phone ───────────────────────────────── */}
          {step === "phone" && (
            <>
              <h2 className="text-xl font-bold text-zinc-900 mb-1">
                Register for the Seminar
              </h2>
              <p className="text-sm text-zinc-500 mb-6">
                {OTP_FLOW_ENABLED
                  ? "OTP verification will be sent to this number"
                  : "Enter your mobile number to continue"}
              </p>

              <label className="block text-sm font-semibold text-zinc-700 mb-2">
                Primary Number <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2">
                <span className="flex items-center px-3 py-3 bg-zinc-50 border border-zinc-200 rounded-xl text-zinc-600 text-sm font-semibold select-none">
                  +91
                </span>
                <input
                  type="tel"
                  maxLength={10}
                  disabled={isOtpVerified}
                  value={phone}
                  onChange={(e) => {
                    setPhone(e.target.value.replace(/\D/g, ""));
                    setError("");
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      if (OTP_FLOW_ENABLED) sendOtp();
                      else continueWithoutOtp();
                    }
                  }}
                  placeholder="Enter mobile number"
                  className="flex-1 px-4 py-3 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#ff9245]/30 focus:border-[#ff9245] transition-colors"
                />
              </div>

              {error && (
                <p className="mt-2.5 text-xs text-red-500 flex items-center gap-1.5">
                  <svg className="h-3.5 w-3.5 shrink-0" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  {error}
                </p>
              )}

              <button
                onClick={OTP_FLOW_ENABLED ? sendOtp : continueWithoutOtp}
                disabled={OTP_FLOW_ENABLED ? loading || isOtpVerified : false}
                className="mt-6 w-full rounded-xl bg-[#ff9245] py-3.5 text-sm font-bold text-white shadow-[0_8px_24px_rgba(255,146,69,0.35)] transition hover:bg-[#f07f2d] hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
              >
                {OTP_FLOW_ENABLED && loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                    Sending OTP…
                  </span>
                ) : (
                  OTP_FLOW_ENABLED ? "Send OTP" : "Continue"
                )}
              </button>
            </>
          )}

          {/* ── STEP 2: OTP ─────────────────────────────────── */}
          {OTP_FLOW_ENABLED && step === "otp" && (
            <>
              <h2 className="text-xl font-bold text-zinc-900 mb-1">
                Verify your number
              </h2>
              <p className="text-sm text-zinc-500 mb-0.5">
                OTP sent to{" "}
                <span className="font-semibold text-zinc-700">+91 {phone}</span>
              </p>
              <button
                onClick={() => {
                  setStep("phone");
                  setOtpDigits(["", "", "", ""]);
                  setError("");
                }}
                className="text-xs text-[#ff9245] font-semibold hover:underline mb-6 block"
              >
                Change number
              </button>

              <div className="flex gap-3 justify-center mb-6">
                {otpDigits.map((d, i) => (
                  <input
                    key={i}
                    ref={otpRefs[i]}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={d}
                    onChange={(e) => handleOtpChange(i, e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Backspace" && !d && i > 0)
                        otpRefs[i - 1].current?.focus();
                      if (e.key === "Enter") verifyOtp();
                    }}
                    className="w-12 h-12 text-center text-xl font-bold border-2 rounded-xl focus:outline-none transition-all duration-200"
                    style={{
                      borderColor: d ? "#ff9245" : "#e4e4e7",
                      background: d ? "#fff7f0" : "white",
                    }}
                  />
                ))}
              </div>

              {error && (
                <p className="mb-4 text-xs text-center text-red-500 flex items-center justify-center gap-1.5">
                  <svg className="h-3.5 w-3.5 shrink-0" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  {error}
                </p>
              )}

              <button
                onClick={verifyOtp}
                disabled={loading}
                className="w-full rounded-xl bg-[#ff9245] py-3.5 text-sm font-bold text-white shadow-[0_8px_24px_rgba(255,146,69,0.35)] transition hover:bg-[#f07f2d] hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                    Verifying…
                  </span>
                ) : (
                  "Verify OTP"
                )}
              </button>

              <p className="mt-4 text-xs text-center text-zinc-500">
                {resendTimer > 0 ? (
                  <>
                    Resend OTP in{" "}
                    <span className="font-semibold text-zinc-700">
                      {resendTimer}s
                    </span>
                  </>
                ) : (
                  <button
                    onClick={() => {
                      if (isOtpVerified) return;
                      setOtpDigits(["", "", "", ""]);
                      setError("");
                      sendOtp();
                    }}
                    disabled={isOtpVerified || loading}
                    className="font-semibold text-[#ff9245] hover:underline"
                  >
                    Resend OTP
                  </button>
                )}
              </p>
            </>
          )}

          {/* ── STEP 3: Registration form ────────────────────── */}
          {step === "form" && (
            <>
              <h2 className="text-xl font-bold text-zinc-900 mb-1">
                Almost there!
              </h2>
              <p className="text-sm text-zinc-500 mb-6">
                Enter &amp; verify your details to register.
              </p>

              <div className="space-y-4">
                {/* Name row */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-zinc-700 mb-1.5">
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="Enter first name"
                      value={form.firstName}
                      onChange={(e) => {
                        setForm((p) => ({ ...p, firstName: e.target.value }));
                        setError("");
                      }}
                      className="w-full px-3 py-2.5 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#ff9245]/30 focus:border-[#ff9245] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-zinc-700 mb-1.5">
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="Enter last name"
                      value={form.lastName}
                      onChange={(e) => {
                        setForm((p) => ({ ...p, lastName: e.target.value }));
                        setError("");
                      }}
                      className="w-full px-3 py-2.5 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#ff9245]/30 focus:border-[#ff9245] transition-colors"
                    />
                  </div>
                </div>

                {/* Verified primary number */}
                <div>
                  <label className="block text-xs font-semibold text-zinc-700 mb-1.5">
                    Primary Number <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-2">
                    <span className="flex items-center px-3 py-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-zinc-500 text-sm font-semibold select-none">
                      +91
                    </span>
                    <div className="relative flex-1">
                      <input
                        type="tel"
                        value={phone.replace(/(\d{5})(\d{5})/, "$1-$2")}
                        readOnly
                        className="w-full px-3 py-2.5 pr-9 border border-green-300 rounded-xl text-sm bg-green-50 text-zinc-700"
                      />
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-green-500"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  </div>
                  {OTP_FLOW_ENABLED && (
                    <p className="mt-1 text-xs text-green-600 font-medium flex items-center gap-1">
                      <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Primary Number verified successfully
                    </p>
                  )}
                </div>

                {/* Parent/Student number */}
                <div>
                  <label className="block text-xs font-semibold text-zinc-700 mb-1.5">
                    Parent/Student Number
                    <span className="ml-1 text-zinc-400 font-normal">(optional)</span>
                  </label>
                  <div className="flex gap-2">
                    <span className="flex items-center px-3 py-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-zinc-500 text-sm font-semibold select-none">
                      +91
                    </span>
                    <input
                      type="tel"
                      maxLength={10}
                      placeholder="Enter number"
                      value={form.parentPhone}
                      onChange={(e) => {
                        setForm((p) => ({
                          ...p,
                          parentPhone: e.target.value.replace(/\D/g, ""),
                        }));
                        setError("");
                      }}
                      className="flex-1 px-3 py-2.5 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#ff9245]/30 focus:border-[#ff9245] transition-colors"
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-xs font-semibold text-zinc-700 mb-1.5">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    placeholder="Enter email"
                    value={form.email}
                    onChange={(e) => {
                      setForm((p) => ({ ...p, email: e.target.value }));
                      setError("");
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleRegister();
                    }}
                    className="w-full px-3 py-2.5 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#ff9245]/30 focus:border-[#ff9245] transition-colors"
                  />
                  <p className="mt-1 text-xs text-zinc-400">
                    Seminar pass will be sent here.
                  </p>
                </div>
              </div>

              {error && (
                <p className="mt-3 text-xs text-red-500 flex items-center gap-1.5">
                  <svg className="h-3.5 w-3.5 shrink-0" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  {error}
                </p>
              )}

              <button
                onClick={handleRegister}
                disabled={loading}
                className="mt-6 w-full rounded-xl bg-[#ff9245] py-3.5 text-sm font-bold text-white shadow-[0_8px_24px_rgba(255,146,69,0.35)] transition hover:bg-[#f07f2d] hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                    Registering…
                  </span>
                ) : (
                  "Register Now"
                )}
              </button>
            </>
          )}

          {/* ── SUCCESS ──────────────────────────────────────── */}
          {step === "success" && (
            <div className="py-4 text-center">
              <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-green-50 border-2 border-green-100">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-8 w-8 text-green-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>

              <h2 className="text-xl font-bold text-zinc-900 mb-2">
                You&apos;re registered! 🎉
              </h2>
              <p className="text-sm text-zinc-500 mb-1">
                Your seminar pass will be sent to{" "}
                <span className="font-semibold text-zinc-700">{form.email}</span>.
              </p>
              <p className="text-sm text-zinc-500 mb-7">
                See you on{" "}
                <span className="font-semibold text-[#ff9245]">
                  {cfg.date}
                </span>{" "}
                at {cfg.venueShort}.
              </p>

              <a
                href={cfg.mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mb-4 inline-flex items-center gap-2 text-xs text-zinc-500 hover:text-[#ff9245] transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="w-3.5 h-3.5"
                >
                  <path
                    fillRule="evenodd"
                    d="M11.54 22.351l.07.04.028.016a.76.76 0 00.723 0l.028-.015.071-.041a16.975 16.975 0 001.144-.742 19.58 19.58 0 002.683-2.282c1.944-2.003 3.5-4.697 3.5-8.327a8.25 8.25 0 00-16.5 0c0 3.63 1.556 6.324 3.5 8.327a19.58 19.58 0 002.683 2.282 16.975 16.975 0 001.144.742zM12 13.5a3 3 0 100-6 3 3 0 000 6z"
                    clipRule="evenodd"
                  />
                </svg>
                {cfg.venueShort} → View on Maps
              </a>

              <br />
              <button
                onClick={() => {
                  onClose();       // close modal
                  router.push("/"); // go to home
                }}
                className="mt-2 rounded-xl bg-[#ff9245] px-10 py-3 text-sm font-bold text-white shadow-[0_8px_24px_rgba(255,146,69,0.35)] transition hover:bg-[#f07f2d] hover:-translate-y-0.5"
              >
                Go to Home
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
